import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  Check,
  ChevronDown,
  Droplets,
  Gauge,
  Home,
  Leaf,
  MessageCircle,
  Search,
  SunMedium,
  Wrench,
  Zap,
} from "lucide-react";
import { useQuote } from "./QuoteModal";
import { DEFAULT_SERVICES, loadServices } from "../Libs/servicesData";
import Seo from "../seo/Seo";
import { PAGE_SEO } from "../seo/pages";
import { breadcrumbSchema, faqSchema } from "../seo/schema";

/* -------------------------------------------------------------------------- */
/*  CONTENT                                                                   */
/*  The list of services comes from Firestore (site/services). If that read   */
/*  fails or takes too long, DEFAULT_SERVICES from servicesData.js is shown.  */
/*  `label` must match the service names in the quote form (site/quote) so    */
/*  the quote opens with the right service already ticked.                    */
/* -------------------------------------------------------------------------- */

const DB_TIMEOUT_MS = 6000;

const FALLBACK_IMG =
  "https://res.cloudinary.com/dj6wdsbtx/image/upload/v1791732731/Golden-Hour_Drilling_in_the_Valley_mcazyh.png";
const HERO_IMG = FALLBACK_IMG;
const CTA_IMG =
  "https://res.cloudinary.com/dj6wdsbtx/image/upload/v1791732733/Solar_Installation_at_Golden_Hour_fpk7wv.png";

// The manager stores each service's icon as a key. Unknown keys fall back to a wrench.
const ICONS = {
  search: Search,
  droplets: Droplets,
  gauge: Gauge,
  sun: SunMedium,
  zap: Zap,
  wrench: Wrench,
  leaf: Leaf,
  home: Home,
};

// Where each service's number sits on the diagram (in the 800 x 460 drawing).
// Only these six ids can appear on the diagram. Any other service the manager
// adds still shows in the explorer, just without a numbered spot.
const SPOTS = {
  survey: [60, 182],
  borehole: [350, 318],
  pump: [350, 402],
  solar: [585, 165],
  solarpump: [405, 212],
  maintenance: [175, 92],
};

// The diagram reads left to right: survey, drill, pump, solar, solar pumping, upkeep.
const JOURNEY_ORDER = ["survey", "borehole", "pump", "solar", "solarpump", "maintenance"];

const FAQS = [
  {
    q: "How do I get a quote?",
    a: "Press any Get a quote button, tell us what you need and where your site is, and we will arrange a site visit and send an itemised quote. It is free.",
  },
  {
    q: "Do you work in my county?",
    a: "We serve homes, farms and businesses across Kenya. Our crews travel to you, and your quote includes the travel to your site.",
  },
  {
    q: "Do I need a survey before drilling?",
    a: "We recommend one. A survey tells you where water is most likely to be found so you are not drilling blind. If you already know your site, say so in the quote form and we will advise.",
  },
  {
    q: "Can I combine services?",
    a: "Yes. Many projects combine a survey, a borehole, a pump and solar power. Tick everything you need in the quote form and we will price it as one project.",
  },
  {
    q: "Can I just buy equipment?",
    a: "Yes. Visit the shop for pumps, panels, tanks and fittings, or choose equipment supply in the quote form.",
  },
];

/* -------------------------------------------------------------------------- */
/*  DATA LOADING                                                              */
/* -------------------------------------------------------------------------- */

const withTimeout = (promise, ms) =>
  Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), ms)),
  ]);

const str = (v, fallback = "") => (typeof v === "string" && v.trim() ? v : fallback);
const strList = (v) =>
  (Array.isArray(v) ? v : []).filter((x) => typeof x === "string" && x.trim());

// Cleans the list so one half-filled service can never break the page:
// every field has a safe value, ids are unique, and the icon is a component.
function normalize(items) {
  const seen = new Set();
  const out = [];

  for (const raw of Array.isArray(items) ? items : []) {
    const id = str(raw?.id);
    const label = str(raw?.label);
    if (!id || !label || seen.has(id)) continue;
    seen.add(id);

    out.push({
      id,
      label,
      short: str(raw.short, label),
      Icon: ICONS[raw.icon] || Wrench,
      summary: str(raw.summary),
      what: str(raw.what),
      included: strList(raw.included),
      goodFor: strList(raw.goodFor),
      img: str(raw.img, FALLBACK_IMG),
      pos: str(raw.pos, "center"),
      alt: str(raw.alt, label),
    });
  }

  return out.length ? out : normalize(DEFAULT_SERVICES);
}

// Renders at once with the defaults, then swaps in the live list when the
// database answers. If it errors or times out, nothing changes.
function useServices() {
  const [services, setServices] = useState(() => normalize(DEFAULT_SERVICES));

  useEffect(() => {
    let alive = true;
    withTimeout(loadServices(), DB_TIMEOUT_MS)
      .then(({ data }) => {
        if (alive) setServices(normalize(data));
      })
      .catch(() => {
        /* keep the default services from servicesData.js */
      });
    return () => {
      alive = false;
    };
  }, []);

  return services;
}

/* -------------------------------------------------------------------------- */
/*  SMALL PIECES                                                              */
/* -------------------------------------------------------------------------- */

function SectionHeading({ title, text, light = false }) {
  return (
    <div>
      <h2
        className={`font-heading text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl lg:text-[2.75rem] ${
          light ? "text-white" : "text-[#0E1726]"
        }`}
      >
        {title}
      </h2>
      {text && (
        <p
          className={`mt-4 max-w-xl text-lg leading-relaxed ${
            light ? "text-white/75" : "text-slate-600"
          }`}
        >
          {text}
        </p>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  DIAGRAM  (a cross-section: survey flag, borehole, pump, tank, solar)      */
/* -------------------------------------------------------------------------- */

function Scene() {
  return (
    <svg
      viewBox="0 0 800 460"
      className="absolute inset-0 h-full w-full"
      role="img"
      aria-label="Cross-section of a site: a survey flag, a borehole with a pump reaching underground water, a water tank, and solar panels powering the pump."
    >
      <defs>
        <linearGradient id="svc-ground" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#1B5A9E" />
          <stop offset="1" stopColor="#0B2A52" />
        </linearGradient>
        <linearGradient id="svc-aquifer" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#1F7BE0" stopOpacity="0.6" />
          <stop offset="1" stopColor="#1F7BE0" stopOpacity="0.2" />
        </linearGradient>
      </defs>

      {/* sky: sun, rays and two soft clouds */}
      <g fill="#fff" opacity="0.09">
        <ellipse cx="380" cy="64" rx="48" ry="14" />
        <ellipse cx="412" cy="54" rx="30" ry="12" />
        <ellipse cx="650" cy="92" rx="40" ry="12" />
        <ellipse cx="676" cy="83" rx="24" ry="10" />
      </g>
      <g transform="translate(90 70)">
        <circle r="26" fill="#F2A93B" />
        <g stroke="#F2A93B" strokeWidth="3" strokeLinecap="round">
          <line x1="36" y1="0" x2="48" y2="0" />
          <line x1="-36" y1="0" x2="-48" y2="0" />
          <line x1="0" y1="36" x2="0" y2="48" />
          <line x1="0" y1="-36" x2="0" y2="-48" />
          <line x1="25" y1="25" x2="34" y2="34" />
          <line x1="-25" y1="25" x2="-34" y2="34" />
          <line x1="25" y1="-25" x2="34" y2="-34" />
          <line x1="-25" y1="-25" x2="-34" y2="-34" />
        </g>
      </g>
      <path
        d="M130 52 C 300 30, 450 88, 560 192"
        fill="none"
        stroke="#F2A93B"
        strokeWidth="2"
        strokeDasharray="3 8"
        strokeLinecap="round"
        opacity="0.55"
      />

      {/* ground and strata */}
      <rect x="0" y="250" width="800" height="210" fill="url(#svc-ground)" />
      <path
        d="M0 292 C 120 282, 240 304, 400 292 S 700 284, 800 296"
        fill="none"
        stroke="#7DB8FF"
        strokeOpacity="0.18"
        strokeWidth="2"
      />
      <path
        d="M0 340 C 140 330, 260 350, 420 338 S 700 332, 800 344"
        fill="none"
        stroke="#7DB8FF"
        strokeOpacity="0.14"
        strokeWidth="2"
      />
      <path
        d="M0 392 C 140 380, 280 402, 420 390 S 700 382, 800 394 V460 H0 Z"
        fill="url(#svc-aquifer)"
      />
      <path
        d="M0 392 C 140 380, 280 402, 420 390 S 700 382, 800 394"
        fill="none"
        stroke="#7DB8FF"
        strokeOpacity="0.7"
        strokeWidth="2"
      />
      <line x1="0" y1="250" x2="800" y2="250" stroke="#7DB8FF" strokeWidth="2" />

      {/* survey flag and test line */}
      <line x1="60" y1="250" x2="60" y2="204" stroke="#fff" strokeWidth="3" strokeLinecap="round" />
      <polygon points="60,204 90,214 60,226" fill="#F2A93B" />
      <path
        d="M60 254 V330"
        stroke="#7DB8FF"
        strokeWidth="2"
        strokeDasharray="3 7"
        strokeLinecap="round"
        opacity="0.7"
      />
      <circle cx="60" cy="332" r="4" fill="#7DB8FF" />

      {/* tank on a stand */}
      <g stroke="#7DB8FF" strokeWidth="4" strokeLinecap="round">
        <line x1="150" y1="190" x2="146" y2="250" />
        <line x1="200" y1="190" x2="204" y2="250" />
      </g>
      <rect x="130" y="120" width="90" height="70" rx="12" fill="#1F7BE0" stroke="#7DB8FF" strokeWidth="3" />
      <rect x="135" y="148" width="80" height="37" rx="8" fill="#7DB8FF" opacity="0.5" />
      <rect x="165" y="112" width="20" height="8" rx="3" fill="#7DB8FF" />

      {/* pipe from the borehole to the tank */}
      <path
        d="M300 250 V175 H220"
        fill="none"
        stroke="#7DB8FF"
        strokeWidth="6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* borehole casing, riser and pump */}
      <rect x="289" y="250" width="22" height="175" fill="#031326" stroke="#7DB8FF" strokeWidth="2" />
      <line x1="300" y1="250" x2="300" y2="385" stroke="#7DB8FF" strokeWidth="4" />
      <rect x="293" y="385" width="14" height="34" rx="4" fill="#F2A93B" />
      <g stroke="#0B1F3A" strokeWidth="1.5">
        <line x1="293" y1="395" x2="307" y2="395" />
        <line x1="293" y1="405" x2="307" y2="405" />
      </g>

      {/* solar panels, legs and the cable to the pump */}
      <g stroke="#7DB8FF" strokeWidth="4" strokeLinecap="round">
        <line x1="575" y1="239" x2="575" y2="250" />
        <line x1="635" y1="217" x2="635" y2="250" />
      </g>
      <polygon points="500,225 590,195 650,212 560,244" fill="#1F7BE0" stroke="#7DB8FF" strokeWidth="3" strokeLinejoin="round" />
      <g stroke="#7DB8FF" strokeWidth="1.5" opacity="0.8">
        <line x1="530" y1="215" x2="590" y2="233" />
        <line x1="560" y1="205" x2="620" y2="223" />
        <line x1="530" y1="234" x2="620" y2="204" />
      </g>
      <path
        d="M505 228 V240 H312 V252"
        fill="none"
        stroke="#F2A93B"
        strokeWidth="3"
        strokeDasharray="2 7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Hotspot({ id, number, active, onSelect, title }) {
  const [x, y] = SPOTS[id];
  return (
    <button
      type="button"
      onClick={() => onSelect(id)}
      aria-label={`${number}. ${title}`}
      aria-pressed={active}
      className="absolute -translate-x-1/2 -translate-y-1/2 focus-visible:outline-none"
      style={{ left: `${(x / 800) * 100}%`, top: `${(y / 460) * 100}%` }}
    >
      {active && (
        <span
          aria-hidden="true"
          className="absolute inset-0 rounded-full bg-[#F2A93B] opacity-60 motion-safe:animate-ping"
        />
      )}
      <span
        className={`relative flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold shadow-lg ring-2 transition-colors duration-150 sm:h-9 sm:w-9 ${
          active
            ? "bg-[#F2A93B] text-[#0B1F3A] ring-white"
            : "bg-white text-[#0A3568] ring-[#0A3568]/40 hover:bg-[#F2A93B]"
        }`}
      >
        {number}
      </span>
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/*  PAGE                                                                      */
/* -------------------------------------------------------------------------- */

export default function Services() {
  const { openQuote } = useQuote();
  const navigate = useNavigate();
  const location = useLocation();
  const reduce = useReducedMotion();
  const services = useServices();

  const explorerRef = useRef(null);
  const [activeId, setActiveId] = useState(null);
  const [hotId, setHotId] = useState("borehole");

  const byId = (id) => services.find((s) => s.id === id);

  // The live list can replace the defaults, so never trust a stored id blindly.
  const svc = byId(activeId) || services[0];
  const idx = services.findIndex((s) => s.id === svc.id);
  const nextSvc = services[(idx + 1) % services.length];

  // Only services that have a spot in the drawing appear on the diagram.
  const journey = JOURNEY_ORDER.map(byId).filter(Boolean);
  const hotSvc = journey.find((s) => s.id === hotId) || journey[0];

  // Lets other pages link straight to a service, e.g. <Link to="/services#solar">
  useEffect(() => {
    const id = location.hash.replace("#", "");
    if (id && services.some((s) => s.id === id)) {
      setActiveId(id);
      setHotId(id);
    }
  }, [location.hash, services]);

  const select = (id) => {
    setActiveId(id);
    explorerRef.current?.scrollIntoView({
      behavior: reduce ? "auto" : "smooth",
      block: "start",
    });
  };

  const onTabKey = (e) => {
    let n = null;
    if (e.key === "ArrowDown" || e.key === "ArrowRight") n = (idx + 1) % services.length;
    if (e.key === "ArrowUp" || e.key === "ArrowLeft") n = (idx - 1 + services.length) % services.length;
    if (n === null) return;
    e.preventDefault();
    setActiveId(services[n].id);
    document.getElementById(`tab-${services[n].id}`)?.focus();
  };

  return (
    <div className="bg-white text-[#0E1726]">
      <Seo
        {...PAGE_SEO.services}
        path="/services"
        schema={[
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Services", path: "/services" },
          ]),
          faqSchema(FAQS),
        ]}
      />

      {/* =============================== HERO =============================== */}
      <section className="relative isolate overflow-hidden bg-[#072445] px-5 pb-16 pt-36 text-white sm:px-8 lg:pb-20 lg:pt-44">
        <motion.img
          src={HERO_IMG}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 -z-20 h-full w-full object-cover"
          initial={{ scale: reduce ? 1 : 1.08 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.2, ease: "easeOut" }}
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[#072445] via-[#072445]/80 to-[#072445]/20" />
        <div className="absolute inset-x-0 bottom-0 -z-10 h-1/3 bg-gradient-to-t from-[#072445] to-transparent" />

        <div className="mx-auto max-w-7xl">
          <h1 className="font-heading max-w-3xl text-4xl font-extrabold leading-[1.06] tracking-tight sm:text-5xl lg:text-6xl">
            Everything for water and solar, in one place.
          </h1>
          <p className="mt-6 max-w-xl text-lg leading-relaxed text-white/85 sm:text-xl">
            Choose a service to see what it involves, then ask for a quote in
            about two minutes.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-4">
            <button
              onClick={() => openQuote()}
              className="group inline-flex items-center gap-2 rounded-xl bg-[#F2A93B] px-7 py-4 font-bold text-[#0B1F3A] transition duration-200 hover:-translate-y-0.5 hover:bg-white hover:shadow-xl"
            >
              Get a free quote
              <ArrowRight
                size={18}
                className="transition-transform duration-300 group-hover:translate-x-1"
              />
            </button>
            <button
              onClick={() =>
                explorerRef.current?.scrollIntoView({
                  behavior: reduce ? "auto" : "smooth",
                  block: "start",
                })
              }
              className="inline-flex items-center gap-2 rounded-xl border border-white/60 px-7 py-4 font-semibold text-white transition duration-200 hover:-translate-y-0.5 hover:bg-white hover:text-[#0A3568] hover:shadow-xl"
            >
              Explore services
              <ChevronDown size={18} />
            </button>
          </div>

          {/* jump straight to a service */}
          <ul className="mt-14 flex flex-wrap gap-2.5">
            {services.map((s) => (
              <li key={s.id}>
                <button
                  onClick={() => select(s.id)}
                  className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-2.5 text-sm font-semibold text-white transition-colors duration-150 hover:bg-white hover:text-[#0A3568]"
                >
                  <s.Icon size={16} />
                  {s.short}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ============================= EXPLORER ============================= */}
      <section
        ref={explorerRef}
        id="explore"
        className="scroll-mt-20 bg-[#F3F7FB] px-5 py-20 sm:px-8 lg:py-28"
      >
        <div className="mx-auto max-w-7xl">
          <SectionHeading
            title="Choose a service"
            text="Select one to see what it is, what is included and who it suits."
          />

          <div className="mt-12 grid gap-6 lg:grid-cols-[300px_1fr] lg:gap-8">
            {/* tabs: a swipeable row on mobile, a list on desktop */}
            <div
              role="tablist"
              aria-label="Services"
              aria-orientation="vertical"
              onKeyDown={onTabKey}
              className="-mx-5 flex gap-2 overflow-x-auto px-5 pb-1 sm:-mx-8 sm:px-8 lg:sticky lg:top-28 lg:mx-0 lg:flex-col lg:self-start lg:overflow-visible lg:px-0 lg:pb-0"
            >
              {services.map((s) => {
                const on = s.id === svc.id;
                return (
                  <button
                    key={s.id}
                    id={`tab-${s.id}`}
                    role="tab"
                    aria-selected={on}
                    aria-controls="service-panel"
                    tabIndex={on ? 0 : -1}
                    onClick={() => setActiveId(s.id)}
                    className={`flex shrink-0 items-center gap-3 rounded-full border px-4 py-3 text-left font-semibold transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1F7BE0] lg:items-start lg:gap-4 lg:rounded-2xl lg:p-4 ${
                      on
                        ? "border-[#0A3568] bg-[#0A3568] text-white"
                        : "border-slate-200 bg-white text-[#0E1726] hover:border-[#1F7BE0]/50"
                    }`}
                  >
                    <s.Icon
                      size={20}
                      className={`shrink-0 lg:mt-0.5 ${on ? "text-[#7DB8FF]" : "text-[#1F7BE0]"}`}
                    />
                    <span className="min-w-0">
                      <span className="block whitespace-nowrap lg:whitespace-normal">
                        {s.label}
                      </span>
                      {s.summary && (
                        <span
                          className={`mt-1 hidden text-sm font-normal leading-snug lg:block ${
                            on ? "text-white/70" : "text-slate-500"
                          }`}
                        >
                          {s.summary}
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* detail panel */}
            <div
              id="service-panel"
              role="tabpanel"
              aria-labelledby={`tab-${svc.id}`}
              className="overflow-hidden rounded-[28px] bg-white shadow-[0_24px_60px_-28px_rgba(4,26,51,0.35)]"
            >
              <div className="relative h-60 bg-[#072445] sm:h-72 lg:h-80">
                <AnimatePresence initial={false}>
                  <motion.img
                    key={svc.id}
                    src={svc.img}
                    alt={svc.alt}
                    className="absolute inset-0 h-full w-full object-cover"
                    style={{ objectPosition: svc.pos }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.35 }}
                  />
                </AnimatePresence>
                <div className="absolute inset-0 bg-gradient-to-t from-[#072445] via-[#072445]/40 to-transparent" />
                <motion.div
                  key={`${svc.id}-title`}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  className="absolute inset-x-0 bottom-0 p-6 text-white sm:p-8"
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-white text-[#0A3568]">
                    <svc.Icon size={24} />
                  </span>
                  <h3 className="font-heading mt-4 text-2xl font-extrabold sm:text-3xl">
                    {svc.label}
                  </h3>
                  {svc.summary && (
                    <p className="mt-1.5 max-w-xl text-white/85">{svc.summary}</p>
                  )}
                </motion.div>
              </div>

              <motion.div
                key={svc.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.18 }}
                className="grid gap-8 p-6 sm:p-8 lg:grid-cols-[1.1fr_1fr] lg:gap-12"
              >
                <div>
                  {svc.what && (
                    <>
                      <h4 className="font-heading text-lg font-bold">What it is</h4>
                      <p className="mt-3 leading-relaxed text-slate-600">{svc.what}</p>
                    </>
                  )}

                  {svc.goodFor.length > 0 && (
                    <>
                      <h4 className="font-heading mt-7 text-lg font-bold">Good for</h4>
                      <ul className="mt-3 flex flex-wrap gap-2">
                        {svc.goodFor.map((g, i) => (
                          <li
                            key={g + i}
                            className="rounded-full bg-[#F3F7FB] px-3.5 py-1.5 text-sm font-semibold text-[#0A3568]"
                          >
                            {g}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>

                {svc.included.length > 0 && (
                  <div>
                    <h4 className="font-heading text-lg font-bold">What is included</h4>
                    <ul className="mt-3 space-y-3">
                      {svc.included.map((item, i) => (
                        <li key={item + i} className="flex gap-3 text-slate-700">
                          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#1F7BE0] text-white">
                            <Check size={12} strokeWidth={3} />
                          </span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </motion.div>

              <div className="flex flex-col gap-4 border-t border-slate-100 bg-[#F8FAFD] p-6 sm:flex-row sm:items-center sm:justify-between sm:px-8">
                <button
                  onClick={() => openQuote({ services: [svc.label] })}
                  className="group inline-flex items-center justify-center gap-2 rounded-xl bg-[#F2A93B] px-6 py-3.5 font-bold text-[#0B1F3A] transition duration-200 hover:-translate-y-0.5 hover:bg-[#0A3568] hover:text-white hover:shadow-xl"
                >
                  Get a quote for {svc.short.toLowerCase()}
                  <ArrowRight
                    size={18}
                    className="transition-transform duration-300 group-hover:translate-x-1"
                  />
                </button>
                {services.length > 1 && (
                  <button
                    onClick={() => select(nextSvc.id)}
                    className="inline-flex items-center justify-center gap-2 font-bold text-[#0A3568] transition hover:gap-3 hover:text-[#1F7BE0]"
                  >
                    Next: {nextSvc.short}
                    <ArrowRight size={18} />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================== DIAGRAM ============================== */}
      {journey.length > 0 && (
        <section className="bg-gradient-to-b from-[#0A3568] to-[#072445] px-5 py-20 text-white sm:px-8 lg:py-28">
          <div className="mx-auto max-w-7xl">
            <SectionHeading
              light
              title="From the ground to your tap"
              text="Water and power work as one system. Tap a number to see where each service fits."
            />

            <div className="mt-12 grid items-center gap-10 lg:grid-cols-[1.35fr_1fr] lg:gap-14">
              <div
                className="relative mx-auto w-full max-w-3xl"
                style={{ aspectRatio: "800 / 460" }}
              >
                <Scene />
                {journey.map((s, i) => (
                  <Hotspot
                    key={s.id}
                    id={s.id}
                    number={i + 1}
                    title={s.label}
                    active={hotSvc.id === s.id}
                    onSelect={setHotId}
                  />
                ))}
              </div>

              <ol className="divide-y divide-white/15 border-y border-white/15">
                {journey.map((s, i) => {
                  const on = hotSvc.id === s.id;
                  return (
                    <li key={s.id}>
                      <button
                        type="button"
                        onClick={() => setHotId(s.id)}
                        aria-expanded={on}
                        className="flex w-full items-center gap-4 py-4 text-left"
                      >
                        <span
                          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold transition-colors duration-150 ${
                            on ? "bg-[#F2A93B] text-[#0B1F3A]" : "bg-white/10 text-white"
                          }`}
                        >
                          {i + 1}
                        </span>
                        <span className={`font-semibold ${on ? "text-white" : "text-white/75"}`}>
                          {s.label}
                        </span>
                      </button>
                      {on && (
                        <motion.div
                          initial={{ opacity: 0, y: -4 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.15 }}
                          className="pb-5 pl-12"
                        >
                          {s.summary && <p className="text-white/80">{s.summary}</p>}
                          <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-3">
                            <button
                              onClick={() => openQuote({ services: [s.label] })}
                              className="inline-flex items-center gap-2 rounded-xl bg-[#F2A93B] px-5 py-2.5 text-sm font-bold text-[#0B1F3A] transition-colors duration-150 hover:bg-white"
                            >
                              Get a quote
                            </button>
                            <button
                              onClick={() => select(s.id)}
                              className="inline-flex items-center gap-2 text-sm font-bold text-[#7DB8FF] transition hover:gap-3 hover:text-white"
                            >
                              Full details
                              <ArrowRight size={16} />
                            </button>
                          </div>
                        </motion.div>
                      )}
                    </li>
                  );
                })}
              </ol>
            </div>
          </div>
        </section>
      )}

      {/* ================================ FAQ ================================ */}
      <section className="px-5 py-20 sm:px-8 lg:py-28">
        <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[1fr_1.4fr] lg:gap-20">
          <div>
            <SectionHeading
              title="Good to know"
              text="Quick answers to the things people ask before requesting a quote."
            />
            <Link
              to="/shop"
              className="group mt-8 inline-flex items-center gap-2 font-bold text-[#0A3568] transition hover:gap-3 hover:text-[#1F7BE0]"
            >
              Looking for equipment? Visit the shop
              <ArrowUpRight size={18} />
            </Link>
          </div>

          <div className="divide-y divide-slate-200 border-y border-slate-200">
            {FAQS.map((f) => (
              <details key={f.q} className="group py-5">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-6 font-heading text-lg font-bold [&::-webkit-details-marker]:hidden">
                  {f.q}
                  <ChevronDown
                    size={22}
                    className="shrink-0 text-[#1F7BE0] transition-transform duration-200 group-open:rotate-180"
                  />
                </summary>
                <p className="mt-3 max-w-2xl leading-relaxed text-slate-600">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ================================ CTA ================================ */}
      <section className="relative isolate overflow-hidden bg-[#072445] px-5 py-20 text-white sm:px-8 lg:py-28">
        <img
          src={CTA_IMG}
          alt=""
          aria-hidden="true"
          loading="lazy"
          className="absolute inset-0 -z-20 h-full w-full object-cover"
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[#072445] via-[#072445]/85 to-[#072445]/40" />
        <div className="mx-auto max-w-7xl">
          <h2 className="font-heading max-w-2xl text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl lg:text-5xl">
            Not sure what you need?
          </h2>
          <p className="mt-4 max-w-xl text-lg leading-relaxed text-white/85">
            Tell us about your site and we will recommend the right mix of
            services, then send you a clear quote.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-4">
            <button
              onClick={() => openQuote()}
              className="group inline-flex items-center gap-2 rounded-xl bg-[#F2A93B] px-7 py-4 font-bold text-[#0B1F3A] transition duration-200 hover:-translate-y-0.5 hover:bg-white hover:shadow-xl"
            >
              Get a free quote
              <ArrowRight
                size={18}
                className="transition-transform duration-300 group-hover:translate-x-1"
              />
            </button>
            <button
              onClick={() => navigate("/", { state: { scrollTo: "contact" } })}
              className="inline-flex items-center gap-2 rounded-xl border border-white/60 px-7 py-4 font-semibold text-white transition duration-200 hover:-translate-y-0.5 hover:bg-white hover:text-[#0A3568] hover:shadow-xl"
            >
              <MessageCircle size={18} />
              Send us a message
            </button>
          </div>
        </div>
      </section>

      <footer className="bg-[#041A33] px-5 pb-32 pt-8 text-sm text-white/60 sm:px-8 lg:pb-8">
        <div className="mx-auto max-w-7xl">
          &copy; {new Date().getFullYear()} MajiMaji Hub. All rights
          reserved.
        </div>
      </footer>
    </div>
  );
}